import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle, Star } from 'lucide-react';
import RewardCelebration, { celebrate } from '../components/RewardCelebration';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FIRST_ORDER_KEY = 'nofee_first_order_celebrated';
const POINTS_ORDER_KEY = 'nofee_order_points_celebrated';

const PaymentSuccess = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [tx, setTx] = useState(null);
  const [order, setOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setTx(res.data);
        if (res.data.status === 'paid') {
          setStatus('paid');
          // Hydrate the inline receipt card (order items + breakdown +
          // restaurant phone). Both calls are public and idempotent;
          // failures are non-fatal — the page still renders the basic
          // "Payment confirmed" state without the receipt detail.
          const orderId = res.data?.order_id;
          if (orderId) {
            const hydrateOrder = (data) => { if (!cancelled) setOrder(data); };
            axios.get(`${API}/orders/${orderId}`).then((r) => hydrateOrder(r.data)).catch(() => {});
            // ── Acceptance polling ───────────────────────────────────────
            // Keep checking the order's `status` field until the
            // restaurant accepts or rejects it. 12s cadence matches the
            // user spec (10–15s). Polling stops on a terminal state
            // (accepted / rejected) or after 30 polls (~6 min) to avoid
            // hammering the API forever if the owner never clicks.
            let orderPolls = 0;
            const pollOrder = async () => {
              if (cancelled) return;
              try {
                const r = await axios.get(`${API}/orders/${orderId}`);
                if (cancelled) return;
                hydrateOrder(r.data);
                const s = r.data?.status;
                const terminal = ['accepted_by_restaurant', 'rejected_by_restaurant', 'cancelled'];
                if (terminal.includes(s)) return;
                orderPolls += 1;
                if (orderPolls < 30) setTimeout(pollOrder, 12000);
              } catch { /* silent — retry on next tick */
                orderPolls += 1;
                if (orderPolls < 30) setTimeout(pollOrder, 12000);
              }
            };
            setTimeout(pollOrder, 12000);
          }
          if (slug) {
            axios.get(`${API}/restaurants/${slug}`).then((r) => {
              if (!cancelled) setRestaurant(r.data?.restaurant || null);
            }).catch(() => { /* silent */ });
          }
          // Loyalty points celebration ($1 spent = N pts). Per-session dedupe
          // so a refresh on the same payment doesn't re-fire.
          try {
            const isCustomer = !!window.localStorage.getItem('nofee_customer_token');
            const cents = Number(res.data?.amount_cents || 0);
            const alreadyPts = window.localStorage.getItem(`${POINTS_ORDER_KEY}_${sessionId}`);
            if (isCustomer && cents > 0 && !alreadyPts) {
              window.localStorage.setItem(`${POINTS_ORDER_KEY}_${sessionId}`, '1');
              // Default to 1 point per dollar (matches default config). Backend is source of truth.
              const dollars = Math.floor(cents / 100);
              if (dollars > 0) {
                setTimeout(() => {
                  celebrate({
                    kind: 'points',
                    title: `+${dollars} Points Earned`,
                    subtitle: 'Loyalty points added to your balance.',
                  });
                }, 1300); // after Welcome (450ms) finishes / sequenced via internal queue
              }
            }
          } catch (_) { /* noop */ }
          // Premium welcome celebration — first real order only (per browser)
          try {
            const already = window.localStorage.getItem(FIRST_ORDER_KEY);
            if (!already) {
              window.localStorage.setItem(FIRST_ORDER_KEY, sessionId || '1');
              setTimeout(() => {
                celebrate({
                  kind: 'generic',
                  title: 'Welcome to NoFee',
                  subtitle: 'Your first order is complete. Rewards unlocked.',
                });
              }, 450);
            }
          } catch (_) { /* localStorage blocked — silently skip */ }
          return;
        }
        attempts += 1;
        if (attempts < 40) {
          setTimeout(poll, 1500);
        } else {
          setStatus('pending');
        }
      } catch (err) {
        setStatus('failed');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <RewardCelebration />
      <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Confirming your payment…</h2>
            <p className="text-slate-500 text-sm">Hold on, this should take just a moment.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment confirmed! 🎉</h2>
            <p className="text-slate-500 mb-4">Your order has been sent to the restaurant. You will receive an email confirmation shortly.</p>

            {/* ───── Restaurant confirmation status banner ─────
                Shows the live acceptance state. Auto-flips when the
                /api/orders/{id} polling loop (every 12s) detects the
                owner clicked Accept Order in their dashboard. */}
            {order && order.status === 'pending_restaurant_confirmation' && (
              <div
                data-testid="order-confirmation-banner-pending"
                className="mb-5 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-left"
              >
                <div className="flex items-center gap-2.5">
                  <Loader2 className="w-4 h-4 text-amber-600 animate-spin shrink-0" />
                  <p className="text-sm font-semibold">Waiting for restaurant confirmation</p>
                </div>
                <p className="text-xs text-amber-800/80 mt-1 pl-6">
                  We&apos;ll notify you the moment the restaurant accepts your order.
                </p>
              </div>
            )}
            {order && order.status === 'accepted_by_restaurant' && (
              <div
                data-testid="order-confirmation-banner-accepted"
                className="mb-5 px-4 py-3 rounded-xl border border-green-200 bg-green-50 text-green-900 text-left"
              >
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-sm font-semibold">Accepted by restaurant</p>
                </div>
                <p className="text-xs text-green-800/80 mt-1 pl-6">
                  The restaurant received your order and will begin preparing it shortly.
                </p>
              </div>
            )}
            {order && order.status === 'rejected_by_restaurant' && (
              <div
                data-testid="order-confirmation-banner-rejected"
                className="mb-5 px-4 py-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-900 text-left"
              >
                <div className="flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <p className="text-sm font-semibold">The restaurant was unable to accept your order</p>
                </div>
                <p className="text-xs text-rose-800/80 mt-1 pl-6">
                  Please contact the restaurant or try another option.
                </p>
              </div>
            )}

            {/* ───── Receipt summary card (P0 — closes the "did it work?"
                loop after Stripe redirects back). Renders as soon as the
                public /api/orders/{id} fetch resolves; gracefully omits
                if the order failed to load. ───── */}
            {order && (
              <div
                data-testid="receipt-summary-card"
                className="text-left mt-2 mb-6 rounded-2xl border border-slate-200 bg-white overflow-hidden"
              >
                <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 border-b border-green-100">
                  <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-green-700">
                    ✓ Payment Receipt
                  </span>
                  <span className="text-xs font-mono text-slate-600">
                    {order.order_number}
                  </span>
                </div>
                <div className="px-4 py-4 space-y-3">
                  {Array.isArray(order.items) && order.items.length > 0 && (
                    <div className="space-y-1.5">
                      {order.items.map((it, idx) => (
                        <div key={idx} className="flex items-start justify-between text-sm gap-3">
                          <span className="text-slate-700 leading-tight">
                            <span className="text-slate-400 font-mono mr-1.5">{it.quantity}×</span>
                            {it.name}
                          </span>
                          <span className="text-slate-900 font-medium tabular-nums shrink-0">
                            ${(Number(it.price || 0) * Number(it.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-t border-slate-100 pt-2.5 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-500">
                      <span>Subtotal</span>
                      <span className="text-slate-700 tabular-nums">${Number(order.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {order.order_type === 'delivery' && (
                      <div className="flex justify-between text-slate-500">
                        <span>Delivery</span>
                        <span className="text-slate-700 tabular-nums">${Number(order.delivery_fee || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(order.tax_amount || 0) > 0 && (
                      <div className="flex justify-between text-slate-500" data-testid="payment-success-sales-tax">
                        <span>
                          Sales Tax
                          {Number(order.tax_rate_pct || 0) > 0 ? ` (${Number(order.tax_rate_pct).toFixed(2)}%)` : ''}
                        </span>
                        <span className="text-slate-700 tabular-nums">${Number(order.tax_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(order.tip_amount || 0) > 0 && (
                      <div className="flex justify-between text-slate-500">
                        <span>Tip</span>
                        <span className="text-slate-700 tabular-nums">${Number(order.tip_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 mt-1 border-t border-slate-100 text-base">
                      <span className="font-bold text-slate-900">Total</span>
                      <span className="font-bold text-gold-600 tabular-nums">${Number(order.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 text-xs">
                    <span className="text-slate-500">
                      Paid via{' '}
                      <span className="text-slate-900 font-semibold capitalize">
                        {(order.payment_method || 'card').replace(/_/g, ' ')}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wide">
                      <CheckCircle2 className="w-3 h-3" /> Paid
                    </span>
                  </div>
                  {(restaurant?.phone || restaurant?.name) && (
                    <div className="pt-3 mt-1 border-t border-slate-100 text-xs text-slate-500">
                      Need to update your order?{' '}
                      {restaurant?.phone ? (
                        <a
                          href={`tel:${restaurant.phone}`}
                          className="text-gold-600 font-semibold hover:underline"
                          data-testid="receipt-restaurant-phone"
                        >
                          Call {restaurant?.name || 'the restaurant'} at {restaurant.phone}
                        </a>
                      ) : (
                        <span className="text-slate-700 font-semibold">{restaurant.name}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!order && tx?.amount_cents && (
              <p className="text-slate-400 text-sm mb-6">Total paid: <span className="text-slate-900 font-semibold">${(tx.amount_cents/100).toFixed(2)}</span></p>
            )}

            {/* Review prompt */}
            {!reviewSubmitted ? (
              <div className="mt-6 mb-6 p-5 bg-slate-50 rounded-2xl border border-slate-100" data-testid="review-prompt">
                <p className="text-sm font-bold text-slate-900 mb-1">How was your experience?</p>
                <p className="text-xs text-slate-500 mb-3">Help others discover great restaurants</p>
                <div className="flex justify-center gap-1 mb-3" onMouseLeave={() => setHoverRating(0)}>
                  {[1,2,3,4,5].map(n => (
                    <button key={n}
                      onMouseEnter={() => setHoverRating(n)}
                      onClick={() => setRating(n)}
                      data-testid={`star-${n}`}
                      className="p-1"
                    >
                      <Star className={`w-7 h-7 transition-colors ${
                        (hoverRating || rating) >= n ? 'fill-gold-500 text-gold-500' : 'text-slate-300'
                      }`} />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <>
                    <input
                      value={reviewName}
                      onChange={e => setReviewName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="w-full px-3 py-2 mb-2 rounded-lg border border-slate-200 text-sm focus:border-gold-500 outline-none"
                      data-testid="review-name"
                    />
                    <textarea
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="Leave a comment (optional)…"
                      className="w-full px-3 py-2 mb-3 rounded-lg border border-slate-200 text-sm resize-none focus:border-gold-500 outline-none"
                      data-testid="review-comment"
                    />
                    <button
                      onClick={async () => {
                        if (!tx?.restaurant_id && !tx?.metadata?.restaurant_id) {
                          // try resolving via slug
                        }
                        setSubmittingReview(true);
                        try {
                          // Look up restaurant id from slug since tx may not have it
                          const restRes = await axios.get(`${API}/restaurants/${slug}`);
                          const restId = restRes.data.restaurant.id;
                          await axios.post(`${API}/reviews`, {
                            restaurant_id: restId,
                            order_id: tx?.order_id || '',
                            customer_name: reviewName.trim() || 'Anonymous',
                            rating,
                            comment: comment.trim(),
                          });
                          setReviewSubmitted(true);
                        } catch (e) {
                          // silent fail; show submitted anyway
                          setReviewSubmitted(true);
                        } finally {
                          setSubmittingReview(false);
                        }
                      }}
                      disabled={submittingReview}
                      className="w-full py-2.5 bg-gold-500 hover:bg-gold-400 disabled:bg-slate-300 text-black font-bold rounded-xl text-sm transition-colors"
                      data-testid="submit-review"
                    >
                      {submittingReview ? 'Sending…' : 'Submit review'}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-6 mb-6 p-4 bg-green-50 rounded-xl border border-green-100 text-sm text-green-700 font-semibold">
                Thanks for your review! 🙏
              </div>
            )}

            <button onClick={() => navigate(`/menu/${slug}`)} className="px-6 py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl transition-colors" data-testid="back-to-menu-btn">
              Back to menu
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment is still processing</h2>
            <p className="text-slate-500 text-sm mb-4">This is taking longer than usual. Don't worry — Stripe will confirm shortly and the restaurant will be notified.</p>
            <button onClick={() => navigate(`/menu/${slug}`)} className="text-gold-600 font-medium text-sm">Back to menu</button>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-9 h-9 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Couldn't confirm payment</h2>
            <p className="text-slate-500 mb-6">Please contact the restaurant or try again.</p>
            <button onClick={() => navigate(`/menu/${slug}`)} className="px-6 py-3 bg-gold-500 hover:bg-gold-400 text-white font-semibold rounded-xl transition-colors">
              Back to menu
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
